import React, { useState, useEffect } from "react";
import { Card, CardBody, Col, Row } from "reactstrap";
import { H3, H5 } from "../../../../AbstractElements";
import axios from "axios";

interface ClientPerformanceData {
  company: string;
  ct: number;
  ytd_rev: number;
  avg_bill: number;
  avg_margin: number;
}

const ClientPerformanceReport = () => {
  const [data, setData] = useState<ClientPerformanceData[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sortConfig, setSortConfig] = useState<{
    key: keyof ClientPerformanceData;
    direction: "asc" | "desc";
  } | null>(null);

  const fetchData = async () => {
    setLoading(true);
    try {
      let params = {};

      const response = await axios.get("/client-performance", { params });

      const results = response.data;
      setData(results);
      setLoading(false);
    } catch (error: any) {
      console.error("Error fetching data:", error);
      setError(error.message);
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []); // Re-fetch when selectedMonth changes

  // Generate month options for dropdown

  const sortData = (key: keyof ClientPerformanceData) => {
    if (!data) return;

    let direction: "asc" | "desc" = "asc";
    if (
      sortConfig &&
      sortConfig.key === key &&
      sortConfig.direction === "asc"
    ) {
      direction = "desc";
    }

    const sortedData = [...data].sort((a, b) => {
      const aVal = a[key];
      const bVal = b[key];

      // If both values are numeric or can be coerced, compare as numbers
      const aNum =
        typeof aVal === "string" && !isNaN(Number(aVal)) ? Number(aVal) : aVal;
      const bNum =
        typeof bVal === "string" && !isNaN(Number(bVal)) ? Number(bVal) : bVal;

      if (aNum < bNum) return direction === "asc" ? -1 : 1;
      if (aNum > bNum) return direction === "asc" ? 1 : -1;
      return 0;
    });

    setData(sortedData);
    setSortConfig({ key, direction });
  };

  if (loading) {
    return <div>Loading Client Performance Report...</div>;
  }

  if (error) {
    return <div>Error: {error}</div>;
  }

  interface FormatCurrency {
    (value: number): string;
  }

  const formatCurrency: FormatCurrency = (value) => {
    if (isNaN(value)) return "-";
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  interface FormatPercentage {
    (value: number): string;
  }

  const formatPercentage: FormatPercentage = (value) => {
    if (isNaN(value)) return "-";
    return `${(value * 1).toFixed(1)}%`;
  };

  return (
    <Col xs='12'>
      <style>{`
        .client-performance-list {
          width: 100%;
          border-collapse: collapse;
          margin: 20px 0;
          font-size: 1rem;
          text-align: left;
        }
        .client-performance-list th, .client-performance-list td {
          padding: 12px 15px;
          border: 1px solid #ddd;
          cursor: pointer;
        }
        .client-performance-list th {
          background-color: #f4f4f4;
          font-weight: bold;
        }
        .client-performance-list .even-row {
          background-color: #f9f9f9;
        }
        .client-performance-list .odd-row {
          background-color: #fff;
        }
        .client-performance-list tr:hover {
          background-color: #f1f1f1;
        }
      `}</style>
      <Row>
        <Col xs='12'>
          <H3 className='font-primary'>Client Performance Report</H3>
        </Col>
      </Row>
      <Row>
        <Col xs='12'>
          <table className='client-performance-list'>
            <thead>
              <tr>
                <th onClick={() => sortData("company")}>Client</th>
                <th onClick={() => sortData("ct")}>Headcount</th>
                <th onClick={() => sortData("ytd_rev")}>YTD Revenue</th>
                <th onClick={() => sortData("avg_bill")}>Average Bill Rate</th>
                <th onClick={() => sortData("avg_margin")}>Gross Margin</th>
              </tr>
            </thead>
            <tbody>
              {data?.map((item, index) => (
                <tr
                  key={index}
                  className={index % 2 === 0 ? "even-row" : "odd-row"}
                >
                  <td>{item.company}</td>
                  <td>{item.ct}</td>
                  <td>{formatCurrency(item.ytd_rev)}</td>
                  <td>{formatCurrency(item.avg_bill)}</td>
                  <td>{formatPercentage(item.avg_margin)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Col>
      </Row>
    </Col>
  );
};

export default ClientPerformanceReport;
